import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  Clock,
  User,
  Target,
  CheckCircle,
  XCircle,
  Eye,
  Plus,
  BookOpen,
  Home,
  Download,
  Lock,
} from 'lucide-react';
import essService from '../../api/services/essService';

const MyDashboard = () => {
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dashboard data from APIs
  const [dashboardData, setDashboardData] = useState({
    employee: {
      name: '',
      employeeId: '',
      department: '',
      position: '',
      profileImage: null
    },
    today: {
      status: null,
      shift: '',
      punchIn: null,
      punchOut: null,
      hoursWorked: '0h 0m',
      location: '',
      anomalies: []
    },
    leave: {
      statistics: {
        totalApplied: 0,
        approved: 0,
        rejected: 0,
        pending: 0,
        cancelled: 0
      },
      nextHoliday: {
        name: '',
        date: '',
        daysLeft: 0
      }
    },
    training: {
      inProgressCourses: [],
      recentCourses: [],
      upcomingSessions: 0,
      totalCourses: 0,
      completed: 0,
      pending: 0,
      inProgress: 0,
      overdue: 0
    }
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Load dashboard data from APIs
  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      // First get profile to extract employeeId
      const profileResult = await essService.getProfile();
      let profile = null;

      if (profileResult.success && profileResult.data) {
        profile = profileResult.data;

        
        // Get name from personalInfo (API returns firstName/lastName in personalInfo)
        const firstName = profile.personalInfo?.firstName || '';
        const lastName = profile.personalInfo?.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim() || 'N/A';

        setDashboardData(prev => ({
          ...prev,
          employee: {
            name: fullName,
            employeeId: profile.employeeId || 'N/A',
            department: profile.employmentInfo?.department || 'N/A',
            position: profile.employmentInfo?.position || 'N/A',
            profileImage: profile.systemInfo?.profilePicture || profile.personalInfo?.profileImage || null
          }
        }));

        // Set shift information
        if (profile.employmentInfo?.shift) {
          const shift = profile.employmentInfo.shift;
          const startTime = shift.startTime ? new Date(`2000-01-01T${shift.startTime}`).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : '';
          const endTime = shift.endTime ? new Date(`2000-01-01T${shift.endTime}`).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : '';
          setDashboardData(prev => ({
            ...prev,
            today: {
              ...prev.today,
              shift: shift.name ? `${shift.name} (${startTime} - ${endTime})` : 'N/A'
            }
          }));
        }
      }

      // Load remaining data in parallel
      const [todayAttendanceResult, leaveBalanceResult, holidaysResult, trainingResult, leaveRequestsResult] = await Promise.allSettled([
        essService.getTodayAttendance(),
        essService.getLeaveBalances(),
        essService.getHolidays(new Date().getFullYear(), null, null),
        essService.getTraining({ type: 'all', limit: 50 }),
        essService.getLeaveRequests()
      ]);

      // Process today's attendance
      if (todayAttendanceResult.status === 'fulfilled' && todayAttendanceResult.value.success) {
        const attendance = todayAttendanceResult.value.data;
        

        if (attendance.hasRecord && attendance.record) {
          const record = attendance.record;
          const checkInTime = record.checkIn
            ? new Date(record.checkIn).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
            : null;
          const checkOutTime = record.checkOut
            ? new Date(record.checkOut).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
            : null;

          // Format working hours - workingHours is in minutes
          let hoursWorked = '0h 0m';
          if (record.workingHours) {
            const hours = Math.floor(record.workingHours / 60);
            const minutes = Math.round(record.workingHours % 60);
            hoursWorked = `${hours}h ${minutes}m`;
          }

          setDashboardData(prev => ({
            ...prev,
            today: {
              ...prev.today,
              status: record.status || 'absent',
              punchIn: checkInTime,
              punchOut: checkOutTime,
              hoursWorked: hoursWorked,
              location: record.checkIn?.location || record.location || 'Office'
            }
          }));
        } else {
          // No attendance record for today
          setDashboardData(prev => ({
            ...prev,
            today: {
              ...prev.today,
              status: 'absent'
            }
          }));
        }
      } else {
        
      }

      // Process leave balances
      if (leaveBalanceResult.status === 'fulfilled') {
        const balanceData = leaveBalanceResult.value;

        // Handle both response formats
        let balances = balanceData.data || balanceData || [];

        // If balances is an object containing a leaveTypes array, use that array
        if (balances && typeof balances === 'object' && !Array.isArray(balances) && Array.isArray(balances.leaveTypes)) {
          balances = balances.leaveTypes;
        }

        if (Array.isArray(balances) && balances.length > 0) {
          const balancesMap = {};

          balances.forEach(balance => {
            const leaveTypeName = (balance.leaveType?.name || balance.type || '').toLowerCase().trim();
            const remainingDays = balance.remainingDays ?? balance.availableDays ?? balance.remaining ?? 0;

            

            // More comprehensive matching for leave types
            // Check for exact matches first, then partial matches
            if (leaveTypeName === 'casual leave' || leaveTypeName === 'casual' || leaveTypeName.includes('casual') || leaveTypeName === 'cl') {
              balancesMap.casualLeave = remainingDays;
            } else if (leaveTypeName === 'sick leave' || leaveTypeName === 'sick' || leaveTypeName.includes('sick') || leaveTypeName === 'sl') {
              balancesMap.sickLeave = remainingDays;
            } else if (leaveTypeName === 'earned leave' || leaveTypeName === 'earned' || leaveTypeName.includes('earned') ||
              leaveTypeName === 'annual leave' || leaveTypeName === 'annual' || leaveTypeName.includes('annual') ||
              leaveTypeName === 'el' || leaveTypeName === 'pl') {
              balancesMap.earnedLeave = remainingDays;
            } else if (leaveTypeName === 'compensatory off' || leaveTypeName === 'compensatory' || leaveTypeName.includes('compensatory') ||
              leaveTypeName === 'comp off' || leaveTypeName === 'comp' || leaveTypeName === 'co') {
              balancesMap.compOff = remainingDays;
            } else {
              // Log unmapped leave types for debugging
              
            }
          });

          setDashboardData(prev => ({
            ...prev,
            leave: {
              ...prev.leave,
              balances: {
                casualLeave: balancesMap.casualLeave ?? 0,
                sickLeave: balancesMap.sickLeave ?? 0,
                earnedLeave: balancesMap.earnedLeave ?? 0,
                compOff: balancesMap.compOff ?? 0
              }
            }
          }));
        } else {
          
        }
      } else {
        
      }

      // Process holidays
      if (holidaysResult.status === 'fulfilled' && holidaysResult.value.success) {
        const holidays = holidaysResult.value.data?.holidays || holidaysResult.value.data || [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const upcomingHolidays = holidays
          .filter(holiday => {
            const holidayDate = new Date(holiday.date);
            holidayDate.setHours(0, 0, 0, 0);
            return holidayDate >= today && holiday.isActive !== false;
          })
          .sort((a, b) => new Date(a.date) - new Date(b.date));

        if (upcomingHolidays.length > 0) {
          const nextHoliday = upcomingHolidays[0];
          const holidayDate = new Date(nextHoliday.date);
          const daysLeft = Math.ceil((holidayDate - today) / (1000 * 60 * 60 * 24));

          setDashboardData(prev => ({
            ...prev,
            leave: {
              ...prev.leave,
              nextHoliday: {
                name: nextHoliday.name || 'Holiday',
                date: nextHoliday.date,
                daysLeft: daysLeft
              }
            }
          }));
        }
      }

      // Process training data
      if (trainingResult.status === 'fulfilled' && trainingResult.value.success) {
        const training = trainingResult.value.data || {};
        const courses = training.courses || [];
        const summary = training.summary || {};

        

        // Get in-progress courses (limit to 1 for display)
        const inProgressCourses = courses
          .filter(course => course.status === 'in_progress' || (course.progress > 0 && course.progress < 100))
          .slice(0, 1)
          .map(course => ({
            id: course.id || course._id,
            name: course.title || course.name || 'Course',
            progress: course.progress || 0,
            nextSession: course.nextSession || course.assignedDate || 'N/A',
            instructor: course.instructor || 'N/A'
          }));

        // Get recent 3 courses (sorted by most recently updated)
        // If courses array is empty, try to get from all courses or use empty array
        let recentCourses = [];
        if (courses && courses.length > 0) {
          // Sort by most recently updated - prioritize updatedAt, then assignedDate, then createdAt
          recentCourses = courses
            .map(course => {
              // Get the most recent updated date for sorting
              const updatedAt = course.updatedAt || course.lastUpdated || null;
              const assignedDate = course.assignedDate || course.assignedAt || null;
              const createdAt = course.createdAt || null;

              // Prioritize updatedAt for "recently updated" sorting
              const sortDate = updatedAt || assignedDate || createdAt;

              return {
                ...course,
                _sortDate: sortDate ? new Date(sortDate) : new Date(0)
              };
            })
            .sort((a, b) => {
              // Sort by most recently updated first (descending)
              return b._sortDate - a._sortDate;
            })
            .slice(0, 3)
            .map(course => ({
              id: course.id || course._id,
              name: course.title || course.name || course.courseTitle || 'Course',
              status: course.status || (course.progress === 100 ? 'completed' : course.progress > 0 ? 'in_progress' : 'pending'),
              progress: course.progress || 0,
              assignedDate: course.assignedDate || course.assignedAt || course.createdAt || null,
              dueDate: course.dueDate || course.deadline || null,
              updatedAt: course.updatedAt || course.lastUpdated || null
            }));
        }

        // Extract statistics from summary
        const totalCourses = summary.totalCourses || courses.length || 0;
        const completed = summary.completed || courses.filter(c => c.status === 'completed').length || 0;
        const inProgress = summary.inProgress || courses.filter(c => c.status === 'in_progress' || (c.progress > 0 && c.progress < 100)).length || 0;
        const pending = courses.filter(c => c.status === 'pending' || c.status === 'not_started' || c.progress === 0).length || 0;
        const overdue = summary.overdue || courses.filter(c => c.status === 'overdue').length || 0;
        const upcomingSessions = summary.upcomingSessions || training.upcomingSessions || 0;

        setDashboardData(prev => ({
          ...prev,
          training: {
            inProgressCourses: inProgressCourses,
            recentCourses: recentCourses,
            upcomingSessions: upcomingSessions,
            totalCourses: totalCourses,
            completed: completed,
            pending: pending,
            inProgress: inProgress,
            overdue: overdue
          }
        }));
      }

      // Process leave requests for statistics
      if (leaveRequestsResult.status === 'fulfilled' && leaveRequestsResult.value.success) {
        const requests = leaveRequestsResult.value.data || [];

        // Calculate leave statistics
        const statistics = {
          totalApplied: requests.length,
          approved: requests.filter(req => req.status === 'approved').length,
          rejected: requests.filter(req => req.status === 'rejected').length,
          pending: requests.filter(req => req.status === 'pending').length,
          cancelled: requests.filter(req => req.status === 'cancelled').length
        };

        

        setDashboardData(prev => ({
          ...prev,
          leave: {
            ...prev.leave,
            statistics: statistics
          }
        }));
      }

    } catch (err) {
      
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return 'text-green-600 bg-green-100';
      case 'absent': return 'text-red-600 bg-red-100';
      case 'wfh': return 'text-blue-600 bg-blue-100';
      case 'late': return 'text-yellow-600 bg-yellow-100';
      case 'available': return 'text-green-600 bg-green-100';
      case 'withheld': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'present': return <CheckCircle className="w-4 h-4" />;
      case 'absent': return <XCircle className="w-4 h-4" />;
      case 'wfh': return <Home className="w-4 h-4" />;
      case 'late': return <Clock className="w-4 h-4" />;
      case 'available': return <Download className="w-4 h-4" />;
      case 'withheld': return <Lock className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const handleQuickAction = (action) => {
    // Navigate to specific page based on action
    switch (action) {
      case 'attendance':
        navigate('/my_attendance');
        break;
      case 'leave':
        navigate('/apply_leave');
        break;
      case 'payslips':
        navigate('/my_payslips');
        break;
      case 'performance':
        navigate('/my_performance');
        break;
      case 'training':
        navigate('/my_training');
        break;
      default:
        
    }
  };

  const renderTodayTile = () => (
    <div className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow cursor-pointer flex flex-col h-full" onClick={() => handleQuickAction('attendance')}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Today</h3>
        <div className="text-right">
          <p className="text-sm text-gray-500">Current Time</p>
          <p className="text-lg font-bold text-gray-900">{formatTime(currentTime)}</p>
        </div>
      </div>

      <div className="space-y-3 flex-1">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Status</span>
          {dashboardData.today.status ? (
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(dashboardData.today.status)}`}>
              {getStatusIcon(dashboardData.today.status)}
              <span className="ml-1 capitalize">{dashboardData.today.status}</span>
            </span>
          ) : (
            <span className="text-sm text-gray-500">Not Available</span>
          )}
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Shift</span>
          <span className="text-sm font-medium text-gray-900">{dashboardData.today.shift || 'N/A'}</span>
        </div>

        {dashboardData.today.punchIn && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Punch In</span>
            <span className="text-sm font-medium text-gray-900">{dashboardData.today.punchIn}</span>
          </div>
        )}

        {dashboardData.today.punchOut && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Punch Out</span>
            <span className="text-sm font-medium text-gray-900">{dashboardData.today.punchOut}</span>
          </div>
        )}

        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Hours Worked</span>
          <span className="text-sm font-medium text-gray-900">{dashboardData.today.hoursWorked}</span>
        </div>

        {dashboardData.today.location && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Location</span>
            <span className="text-sm font-medium text-gray-900">{dashboardData.today.location}</span>
          </div>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200">
        <button
          onClick={() => handleQuickAction('attendance')}
          className="w-full flex items-center justify-center space-x-2 px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50"
        >
          <Eye className="w-4 h-4" />
          <span>View Attendance</span>
        </button>
      </div>
    </div>
  );

  const renderLeaveTile = () => (
    <div className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow cursor-pointer flex flex-col h-full" onClick={() => handleQuickAction('leave')}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Leave</h3>
        {dashboardData.leave.statistics.pending > 0 && (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            {dashboardData.leave.statistics.pending} Pending
          </span>
        )}
      </div>

      <div className="space-y-3 flex-1">
        <div className="grid grid-cols-2 gap-3">
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <p className="text-2xl font-bold text-blue-600">{dashboardData.leave.statistics.totalApplied}</p>
            <p className="text-xs text-gray-600">Applied</p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <p className="text-2xl font-bold text-green-600">{dashboardData.leave.statistics.approved}</p>
            <p className="text-xs text-gray-600">Approved</p>
          </div>
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <p className="text-2xl font-bold text-red-600">{dashboardData.leave.statistics.rejected}</p>
            <p className="text-xs text-gray-600">Rejected</p>
          </div>
          <div className="text-center p-3 bg-yellow-50 rounded-lg">
            <p className="text-2xl font-bold text-yellow-600">{dashboardData.leave.statistics.pending}</p>
            <p className="text-xs text-gray-600">Pending</p>
          </div>
        </div>

        {dashboardData.leave.statistics.cancelled > 0 && (
          <div className="text-center p-2 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-700">
              <span className="text-gray-600">Cancelled: </span>
              <span className="font-bold">{dashboardData.leave.statistics.cancelled}</span>
            </p>
          </div>
        )}

        {dashboardData.leave.nextHoliday.name && (
          <div className="p-3 bg-yellow-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-yellow-600" />
              <div>
                <p className="text-sm font-medium text-yellow-800">Next Holiday</p>
                <p className="text-sm text-yellow-700">{dashboardData.leave.nextHoliday.name} - {dashboardData.leave.nextHoliday.daysLeft} days</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200">
        <button
          onClick={() => handleQuickAction('leave')}
          className="w-full flex items-center justify-center space-x-2 px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50"
        >
          <Plus className="w-4 h-4" />
          <span>Apply Leave</span>
        </button>
      </div>
    </div>
  );

  const renderTrainingTile = () => (
    <div className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow cursor-pointer flex flex-col h-full" onClick={() => handleQuickAction('training')}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Training</h3>
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {dashboardData.training.upcomingSessions} Sessions
        </span>
      </div>

      <div className="space-y-3 flex-1">
        {/* Training Statistics */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="p-2 bg-blue-50 rounded-lg text-center">
            <p className="text-xl font-bold text-blue-600">{dashboardData.training.totalCourses}</p>
            <p className="text-xs text-gray-600">Total</p>
          </div>
          <div className="p-2 bg-green-50 rounded-lg text-center">
            <p className="text-xl font-bold text-green-600">{dashboardData.training.completed}</p>
            <p className="text-xs text-gray-600">Completed</p>
          </div>
          <div className="p-2 bg-yellow-50 rounded-lg text-center">
            <p className="text-xl font-bold text-yellow-600">{dashboardData.training.inProgress}</p>
            <p className="text-xs text-gray-600">In Progress</p>
          </div>
          <div className="p-2 bg-orange-50 rounded-lg text-center">
            <p className="text-xl font-bold text-orange-600">{dashboardData.training.pending}</p>
            <p className="text-xs text-gray-600">Pending</p>
          </div>
        </div>

        {/* Overdue indicator */}
        {dashboardData.training.overdue > 0 && (
          <div className="p-2 bg-red-50 rounded-lg border border-red-200">
            <p className="text-xs text-red-700 font-medium">
              ⚠️ {dashboardData.training.overdue} course{dashboardData.training.overdue > 1 ? 's' : ''} overdue
            </p>
          </div>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200">
        <button
          onClick={() => handleQuickAction('training')}
          className="w-full flex items-center justify-center space-x-2 px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50"
        >
          <BookOpen className="w-4 h-4" />
          <span>View Training</span>
        </button>
      </div>
    </div>
  );


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading dashboard...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
            <button
              onClick={loadDashboardData}
              className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
              <p className="text-gray-600">Welcome back, {dashboardData.employee.name || 'Employee'}!</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Employee ID</p>
              <p className="text-lg font-semibold text-gray-900">{dashboardData.employee.employeeId}</p>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <User className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-500">Department</p>
                <p className="text-lg font-semibold text-gray-900 truncate" title={dashboardData.employee.department}>
                  {dashboardData.employee.department}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Target className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Position</p>
                <p className="text-lg font-semibold text-gray-900">{dashboardData.employee.position}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Current Time</p>
                <p className="text-lg font-semibold text-gray-900">{formatTime(currentTime)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Dashboard Tiles */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
          {renderTodayTile()}
          {renderLeaveTile()}
          {renderTrainingTile()}
        </div>

        {/* Recent Courses Table */}
        <div className="mt-8">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Recent Courses</h3>
              <button
                onClick={() => handleQuickAction('training')}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                View All
              </button>
            </div>
            {dashboardData.training.recentCourses.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Course
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Progress
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Assigned
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Due
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {dashboardData.training.recentCourses.map((course) => {
                      const getStatusColor = (status) => {
                        switch (status) {
                          case 'completed': return 'bg-green-100 text-green-800';
                          case 'in_progress': return 'bg-blue-100 text-blue-800';
                          case 'overdue': return 'bg-red-100 text-red-800';
                          case 'pending': return 'bg-yellow-100 text-yellow-800';
                          default: return 'bg-gray-100 text-gray-800';
                        }
                      };

                      const formatDate = (dateString) => {
                        if (!dateString) return 'N/A';
                        try {
                          const date = new Date(dateString);
                          return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                        } catch {
                          return 'N/A';
                        }
                      };

                      return (
                        <tr key={course.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleQuickAction('training')}>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{course.name}</div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(course.status)}`}>
                              {course.status.replace('_', ' ').toUpperCase()}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-full bg-gray-200 rounded-full h-2 mr-2" style={{ minWidth: '60px' }}>
                                <div
                                  className={`h-2 rounded-full ${course.progress === 100 ? 'bg-green-600' :
                                    course.progress > 0 ? 'bg-blue-600' : 'bg-gray-300'
                                    }`}
                                  style={{ width: `${course.progress}%` }}
                                ></div>
                              </div>
                              <span className="text-sm text-gray-600">{course.progress}%</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(course.assignedDate)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(course.dueDate)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No courses available</p>
                <button
                  onClick={() => handleQuickAction('training')}
                  className="mt-4 text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  View Training
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyDashboard;
